/* sticky-buy-bar.js — reveals snippets/sticky-buy-bar.liquid once the buy box
 * has scrolled off the top, and keeps it honest about what the buy box says.
 *
 * This file owns NO state. Quantity lives in the buy box's `quantity` input,
 * the cart lives in window.SydneyTaps.cart, the project list lives in
 * project-list.js, the selected variant lives in the form's hidden [data-variant-id].
 * Everything below either mirrors one of those or forwards to it.
 *
 * HOW THE OBSERVER IS SET UP, AND THE BUG THAT SHAPED IT
 * An IntersectionObserver reports "not intersecting" both for an element that
 * is ABOVE the viewport and for one still BELOW it. On mobile the buy box
 * starts below the fold (the gallery is above it), so a bare !isIntersecting
 * reveals the bar at the very top of the page — the one place it must not be.
 *
 * The first fix here compared entry.boundingClientRect.bottom against the root
 * top, which reads correctly but does not FIRE correctly: an observer only
 * notifies when the threshold index changes, and "below the fold" and
 * "scrolled past" are both ratio 0, i.e. the same index. Measured at 390 in CI
 * (run 34440106068): the bar never appeared, because a jump from below-fold to
 * past-the-box never crossed a threshold and no callback ran at all. That is a
 * real defect, not a test artefact — an anchor jump to the specs, a restored
 * scroll position or a find-in-page lands a shopper past the buy box the same
 * way.
 *
 * So the root is grown downwards instead. With a bottom rootMargin larger than
 * any page, the anchor intersects that expanded root whenever it is at or
 * below the viewport top, and stops intersecting exactly when its bottom edge
 * passes above it. Both directions are now real threshold crossings, and
 * `!isIntersecting` alone is precisely "scrolled past". Still one observer, no
 * scroll listener, no offsets. */
(() => {
  'use strict';
  const bar = document.querySelector('[data-sbb]');
  if (!bar) return;
  const section = document.querySelector('.product[data-section-id]');
  if (!section) return;

  const mode = bar.dataset.sbbMode || 'online';
  const $ = (s, r = document) => r.querySelector(s);

  /* chat state, declared here rather than beside the chat block below: setOn()
   * is called once during setup, before that block is reached, and a `let` read
   * from a hoisted function before its declaration is a TDZ ReferenceError — it
   * would take the whole bar down, quantity and Add to cart included. */
  const chatWrap = $('[data-sbb-chat]', bar);
  const chatBtn = $('[data-sbb-chat-open]', bar);
  let chatEl = null;
  let chatFound = null;   // the element, held until it is safe to reveal
  let chatMo = null;
  let chatWatch = null;

  /* ---------- what we mirror ---------- */
  const form = $('[data-product-form] form', section);
  const qtyEl = form && $('[data-qty-input]', form);
  const realDown = form && $('[data-qty-down]', form);
  const realUp = form && $('[data-qty-up]', form);
  const realAdd = form && $('[data-add]', form);
  const realAddText = form && $('[data-add-text]', form);

  /* ---------- reveal ---------- */
  // the purchase controls, in the order the section can render them
  const anchor = $('.product__buy', section)
    || $('.product__cfp-actions', section)
    || $('.product__info', section);
  if (!anchor) return;

  const RESERVE = 'sbb-on'; // on <body>: reserves the bar's height so the footer stays reachable
  let on = null;
  const setOn = (next) => {
    if (next === on) return;
    on = next;
    bar.classList.toggle('is-on', next);
    document.body.classList.toggle(RESERVE, next);
    if (next) measureChatClearance(); else revealChat();
    // visibility:hidden in the off state already removes the bar from the tab
    // order and the accessibility tree; `inert` is the belt to that brace, so
    // there is never a moment when this Add to cart and the real one are both
    // exposed. See the decision record for why it is exposed at all.
    if ('inert' in HTMLElement.prototype) bar.inert = !next;
    bar.setAttribute('aria-hidden', next ? 'false' : 'true');
  };
  setOn(false);

  // bottom margin larger than any product page: the expanded root's TOP is
  // still the viewport top, so not-intersecting <=> the anchor's bottom edge
  // has passed above the viewport top. See the header note.
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) setOn(!e.isIntersecting);
  }, { rootMargin: '0px 0px 100000px 0px', threshold: 0 });
  io.observe(anchor);

  /* ---------- price follows the selected variant ---------- */
  const priceEl = $('[data-sbb-price]', bar);
  const wasEl = $('[data-sbb-was]', bar);
  const imgEl = $('[data-sbb-img]', bar);
  const fmtMoney = (c) => {
    try {
      return (c / 100).toLocaleString('en-AU', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD',
      });
    } catch { return '$' + (c / 100).toFixed(2); }
  };
  // ui.js bubbles variant:change to document with the variant as detail —
  // the same event product.js re-prices the buy box from.
  document.addEventListener('variant:change', (e) => {
    const v = e.detail;
    if (!v || !priceEl) return;
    priceEl.textContent = fmtMoney(v.price);
    if (wasEl) {
      // $1.00 saving floor, as the buy box (window.STBadges.isSaving, cents)
      const B = window.STBadges;
      const disc = !!(B && B.isSaving && B.isSaving(v.price, v.compare_at_price, 'cents'));
      wasEl.textContent = disc ? fmtMoney(v.compare_at_price) : '';
      wasEl.hidden = !disc;
    }
    if (imgEl && v.featured_image && v.featured_image.src) imgEl.src = v.featured_image.src;
    syncAdd();
  });

  /* ---------- add to cart: submit the real form, nothing else ---------- */
  const addBtn = $('[data-sbb-add]', bar);
  const addText = $('[data-sbb-add-text]', bar);
  function syncAdd() {
    if (!addBtn || !realAdd) return;
    addBtn.disabled = realAdd.disabled;
    if (addText && realAddText) addText.textContent = realAddText.textContent;
  }
  if (addBtn && form) {
    addBtn.addEventListener('click', () => {
      // requestSubmit fires a real submit event, which theme.js's delegated
      // listener intercepts: formItem() reads `quantity` + the selected
      // variant, formHint() builds the optimistic line, and the cart store
      // paints the drawer in the first frame. Identical to pressing the buy
      // box's own button — including the no-island fallback (theme.js lets
      // the native post through when the cart module failed to load).
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
    syncAdd();
  }

  /* ---------- quantity: read the form's input, drive the real stepper ---------- */
  const qtyWrap = $('[data-sbb-qty]', bar);
  const qtyOut = $('[data-sbb-qty-out]', bar);
  const qtyChip = $('[data-sbb-qtychip]', bar);
  const readQty = () => {
    const n = parseInt(String(qtyEl ? qtyEl.value : '1').replace(/[^\d]/g, ''), 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  };
  function paintQty() {
    if (!qtyEl) return;
    const n = readQty();
    if (qtyOut && qtyOut.textContent !== String(n)) qtyOut.textContent = String(n);
    // mirror the real stepper's disabled state (product.js owns the inventory cap)
    const d = $('[data-sbb-qty-down]', bar);
    const u = $('[data-sbb-qty-up]', bar);
    if (d && realDown) d.disabled = realDown.disabled;
    if (u && realUp) u.disabled = realUp.disabled;
    // the chip is the narrow layout's stand-in for the dropped stepper: it says
    // "x3" so an Add from the bar can never look like it is adding one
    if (qtyChip) {
      qtyChip.textContent = n > 1 ? '×' + n : '';
      qtyChip.hidden = n <= 1;
    }
  }
  if (qtyEl && qtyWrap) {
    qtyWrap.hidden = false;
    // forward to the REAL buttons: every clamp, cap and "Only N available"
    // message in product.js runs exactly as if the buy box had been used
    $('[data-sbb-qty-down]', bar)?.addEventListener('click', () => { realDown?.click(); paintQty(); });
    $('[data-sbb-qty-up]', bar)?.addEventListener('click', () => { realUp?.click(); paintQty(); });
    qtyEl.addEventListener('change', paintQty);
    qtyEl.addEventListener('input', paintQty);
    // product.js resets the field to 1 on a confirmed add without firing `change`
    form?.addEventListener('cart:added', () => requestAnimationFrame(paintQty));
    paintQty();
  }

  /* ---------- configurator: jump back to the builder ---------- */
  if (mode === 'configurator') {
    $('[data-sbb-configure]', bar)?.addEventListener('click', () => {
      const target = section.querySelector('[data-configurator], .cfg, .shopify-app-block') || $('.product__info', section);
      if (!target) return;
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });
  }

  /* ---------- chat: the bar's own launcher ----------------------------------
   * Luke, 2026-09-15, desktop PDP: "chat button widget currently sits on top of
   * the sticky add to cart/project bar, we need a better solution, or have chat
   * become part of the bar, perhaps a separator on the end and then chat".
   *
   * WHAT SHOPIFY'S CHAT ACTUALLY IS, AND WHAT IT EXPOSES. The app embed
   * (shopify://apps/inbox/blocks/chat) loads
   * cdn.shopify.com/storefront/web-components/chat.js as a module and appends a
   * single <shopify-chat> element to <body>. Read off the live store on
   * 2026-09-15:
   *   - it is a custom element with an OPEN shadow root;
   *   - class AgentShell exposes show(), close(), and an `open` property with a
   *     getter and a setter, plus an observed `open` ATTRIBUTE. Calling show()
   *     lazily imports the chat runtime and opens the real panel — verified,
   *     with the panel rendered and `open` reflected;
   *   - it hides its own activator while open (:host([open]) .activator);
   *   - attribute changes on the host are observable from outside it, so the
   *     open/closed state can be mirrored without polling.
   * So a programmatic open IS reliably possible and this is the preferred
   * solution, not the offset-only fallback.
   *
   * PRESENCE GATE. The item ships `hidden` in the Liquid and is revealed only
   * once a real <shopify-chat> is in the document: a store with the app embed
   * turned off renders no Chat button at all rather than one that does nothing.
   * The same latch adds body.sbb-chat, which is what the CSS keys Inbox's own
   * activator off — so the launcher is never hidden unless a working
   * replacement is on screen.
   *
   * WHY THE REVEAL WAITS FOR THE BAR TO BE OFF. chat.js is async and
   * low-priority; it can land a second or two after first paint, which on a
   * restored scroll position or an anchor jump is a moment when the bar is
   * already shown. Growing the actions row under the shopper's eyes would
   * shorten the title and shift the row — a real shift in a fixed element, and
   * the transition/CLS budget is measured on this page. So the item is revealed
   * on the next frame in which the bar is NOT shown (which is the normal case:
   * a PDP opens above the fold). If that never happens, the shopper still has
   * chat: the CSS lifts Inbox's own launcher clear of the bar whenever sbb-on
   * is set, regardless of this latch.
   *
   * WHAT IT DOES NOT DO. It does not touch Inbox's settings (position, offset,
   * icon and label are the merchant's, in the app's admin), does not reach into
   * the shadow root to click anything, and does not reimplement any part of the
   * panel. */
  // Inbox's activator rests --shopify-chat-activator-offset above the viewport
  // bottom; give it the bar's real height plus whatever sits between the bar and
  // the bottom edge (the 64px mobile nav) plus that same 20px resting gap.
  // Measured rather than assumed, because the height carries
  // env(safe-area-inset-bottom) and that is a number only the device knows.
  //
  // offsetHeight and the COMPUTED `bottom`, not getBoundingClientRect(): the bar
  // reveals itself with transform: translateY(100%) over --dur-med, and a rect
  // read during that transition is the ANIMATING box. Measured at 390 on preview
  // theme 165492195363, called from setOn(true): the rect said 85px where the
  // truth is 148px (64px nav + 64px bar + 20px) — the offset would have parked
  // the launcher inside the bar. offsetHeight ignores transforms and the used
  // `bottom` is the .sbb{bottom} the breakpoint set, so this is transition-proof
  // and can be called at any moment. The CSS carries the same sum as a static
  // calc() for the frames before this runs.
  function measureChatClearance() {
    const h = bar.offsetHeight;
    if (!h) return;
    const below = parseFloat(getComputedStyle(bar).bottom) || 0;
    document.body.style.setProperty('--sbb-chat-offset', Math.round(h + below + 20) + 'px');
  }

  /* ---------- the panel pops out of the bar (2026-09-16) ----------------
   * Luke, PDP with the bar shown and the panel open over the footer: "can we
   * have this appear as if it pops out of the sticky bar in cases where it's
   * triggered from here?"
   *
   * Inbox's panel (part="dialog") is absolutely positioned inside the fixed
   * host: bottom = --shopify-chat-activator-offset, right = 20px, and it
   * opens by scaling up from `right bottom` (@starting-style scale .2 →
   * 1, read off chat.js on 2026-09-16). So it already grows out of a
   * bottom-right point; the two things that make it float instead of pop
   * are the 20px resting gap the clearance leaves above the bar and the
   * 20px viewport inset that ignores where the Chat button actually is.
   * body.sbb-chat-from closes both in CSS: bottom edge flush with the bar's
   * top, right edge on the button's right edge (--sbb-chat-right, measured
   * here), corners squared where it meets the bar. The class is set ONLY by
   * this button — "in cases where it's triggered from here" — and dropped
   * the moment the panel closes, whoever closed it, so a panel opened from
   * Inbox's own launcher above the fold keeps its stock position. */
  function measureChatAnchor() {
    if (!chatBtn) return;
    const r = chatBtn.getBoundingClientRect();
    if (!r.width) return;
    document.body.style.setProperty('--sbb-chat-right', Math.max(0, Math.round(window.innerWidth - r.right)) + 'px');
  }
  const FROM_BAR = 'sbb-chat-from';

  function paintChat() {
    if (!chatBtn || !chatEl) return;
    const isOpen = chatEl.hasAttribute('open');
    if (!isOpen && document.body.classList.contains(FROM_BAR)) {
      document.body.classList.remove(FROM_BAR);
      document.body.style.removeProperty('--sbb-chat-right');
    }
    chatBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    // the VISIBLE label never changes (see the theme.css note): a "Close"
    // wider than "Chat" would move the whole row every time the panel opens.
    chatBtn.setAttribute('aria-label', isOpen
      ? (chatBtn.dataset.labelClose || 'Close chat')
      : (chatBtn.dataset.labelOpen || 'Chat'));
  }

  function revealChat() {
    if (!chatFound || !chatWrap || !chatBtn || chatEl) return;
    if (on) return; // never grow the row while the shopper is looking at it
    chatEl = chatFound;
    chatFound = null;
    chatWrap.hidden = false;
    document.body.classList.add('sbb-chat');
    chatBtn.addEventListener('click', () => {
      if (!chatEl) return;
      const isOpen = chatEl.hasAttribute('open');
      // show()/close() are shopify-chat's own public methods; the `open`
      // attribute is its observed fallback if they are ever withdrawn.
      if (isOpen) {
        if (typeof chatEl.close === 'function') chatEl.close();
        else chatEl.removeAttribute('open');
      } else {
        // anchor first, so the very first frame of the open transition already
        // grows out of the button rather than jumping there after it
        measureChatAnchor();
        document.body.classList.add(FROM_BAR);
        if (typeof chatEl.show === 'function') chatEl.show();
        else chatEl.setAttribute('open', '');
      }
      paintChat();
    });
    chatMo = new MutationObserver(paintChat);
    chatMo.observe(chatEl, { attributes: true, attributeFilter: ['open'] });
    paintChat();
  }

  function foundChat(el) {
    if (chatEl || chatFound) return;
    chatFound = el;
    revealChat();
  }

  if (chatWrap && chatBtn) {
    const already = document.querySelector('shopify-chat');
    if (already) foundChat(already);
    else {
      // chat.js appends <shopify-chat> straight to <body>, so a childList
      // observer on <body> alone catches it — no subtree walk of a page this
      // theme mutates constantly.
      chatWatch = new MutationObserver(() => {
        const el = document.querySelector('shopify-chat');
        if (!el) return;
        chatWatch.disconnect(); chatWatch = null;
        foundChat(el);
      });
      chatWatch.observe(document.body, { childList: true });
      // the app embed is off, or its script never arrived: stop watching rather
      // than holding an observer for the life of the page.
      setTimeout(() => { if (chatWatch) { chatWatch.disconnect(); chatWatch = null; } }, 30000);
    }
  }

  // the bar's height changes with the breakpoint, and the safe-area inset with
  // orientation; keep the clearance honest while the bar is shown
  addEventListener('resize', () => {
    if (on) measureChatClearance();
    if (document.body.classList.contains(FROM_BAR)) measureChatAnchor();
  }, { passive: true });

  /* ---------- teardown ---------- */
  // instant-nav swaps documents; drop the reserve so it cannot leak onto a
  // page that has no bar
  window.addEventListener('pagehide', () => {
    document.body.classList.remove(RESERVE, 'sbb-chat', FROM_BAR);
    document.body.style.removeProperty('--sbb-chat-offset');
    document.body.style.removeProperty('--sbb-chat-right');
    io.disconnect();
    chatMo?.disconnect();
    chatWatch?.disconnect();
  });
})();
